/**
 * Kono-only panel on the quote page: for every quoted item, the vendor
 * filling it, every other platform option that could fill the line, the
 * vendors suggested for it (with the source and date of each recommendation)
 * and the vendors actually contacted, with where each request stands. RFQ
 * lines that are not on the quote — still being sourced — are listed too.
 *
 * Loaded on demand: it runs the live inventory match and vendor search for
 * every line, which takes a few seconds.
 */
import { useState } from "react";
import {
  QuoteItemSourcing,
  QuoteLineSourcing,
  QuoteSourcing,
  QuoteVendorRequestStatus,
  QuoteVendorRequestView,
  loadQuoteSourcing,
  messageOf,
} from "./api";
import type { RfqLineOption, RfqVendorSuggestion } from "../rfq/api";
import { FitTable } from "../rfq/FitTable";
import { VendorReply } from "../rfq/VendorReply";
import { VendorProvenanceChip } from "../rfq/VendorProvenance";
import type { ProductInsight } from "../components/SuperUser/superUserApi";
import {
  ItemFieldsSummary,
  PriceStory,
  ProvenanceSection,
  RecentChanges,
  SuperUserPricePanel,
} from "../components/SuperUser/SuperUserPricePanel";
import { useSuperUserMode } from "../buyerStateDefs";

const when = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const STATUS_STYLE: Record<QuoteVendorRequestStatus, string> = {
  queued: "bg-gray-100 text-gray-700",
  sent: "bg-amber-100 text-amber-800",
  responded: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  closed: "bg-gray-200 text-gray-700",
};

const STATUS_LABEL: Record<QuoteVendorRequestStatus, string> = {
  queued: "Queued",
  sent: "Sent, awaiting reply",
  responded: "Replied",
  declined: "Declined",
  closed: "Closed",
};

const VERDICT_STYLE: Record<string, string> = {
  exact: "bg-green-100 text-green-800",
  close: "bg-amber-100 text-amber-800",
  unavailable: "bg-red-100 text-red-800",
};

const VERDICT_LABEL: Record<string, string> = {
  exact: "Exact match on the platform",
  close: "Close matches only",
  unavailable: "Not on the platform",
};

const SOURCE_STYLE: Record<string, string> = {
  llm: "bg-violet-100 text-violet-800",
  "line-card": "bg-emerald-100 text-emerald-800",
  "past-rfq": "bg-sky-100 text-sky-800",
  archive: "bg-amber-100 text-amber-800",
  inventory: "bg-gray-100 text-gray-700",
  catalog: "bg-teal-100 text-teal-800",
};

const sourceLabel = (s: RfqVendorSuggestion): string =>
  s.sourceLabel ||
  (
    {
      inventory: "Inventory catalog",
      "line-card": "Verified line card",
      "past-rfq": "Past RFQ reply",
      archive: "California Metals archive",
      llm: "AI suggestion (unverified)",
      catalog: "Vendor catalog (operator pick)",
    } as Record<string, string>
  )[s.source || "inventory"];

const Chip = ({
  className,
  children,
  title,
}: {
  className: string;
  children: React.ReactNode;
  title?: string;
}) => (
  <span
    className={`inline-block rounded-full px-1.5 py-0.5 text-[0.65rem] font-medium ${className}`}
    title={title}
  >
    {children}
  </span>
);

const RequestStatus = ({ request }: { request: QuoteVendorRequestView }) => (
  <span className="text-xs text-gray-600">
    <Chip
      className={STATUS_STYLE[request.status] || "bg-gray-100 text-gray-700"}
    >
      {STATUS_LABEL[request.status] || request.status}
    </Chip>{" "}
    {request.status === "queued" && request.queuedUntil
      ? `sends ${when(request.queuedUntil)}`
      : request.sentAt
        ? `sent ${when(request.sentAt)}`
        : ""}
    {request.followUpsSent > 0 &&
      ` · ${request.followUpsSent} follow-up${request.followUpsSent === 1 ? "" : "s"}`}
    {request.status === "sent" &&
      request.nextFollowUpAt &&
      ` · next follow-up ${when(request.nextFollowUpAt)}`}
    {request.respondedAt && ` · replied ${when(request.respondedAt)}`}
    {(request.responseSummary || request.responseText) && (
      <span className="block">
        <VendorReply
          status={request.status}
          declineKind={request.declineKind}
          declineReason={request.declineReason}
          responseSummary={request.responseSummary}
          responseText={request.responseText}
        />
      </span>
    )}
    {request.emails.length > 0 && (
      <span className="block text-gray-400">{request.emails.join(", ")}</span>
    )}
  </span>
);

const OptionsTable = ({
  options,
  currency,
}: {
  options: RfqLineOption[];
  currency: string;
}) => (
  <table className="mt-1 w-full text-xs">
    <thead>
      <tr className="text-left text-gray-500">
        <th className="py-1 pr-2">Vendor</th>
        <th className="py-1 pr-2">Item</th>
        <th className="py-1 pr-2">Price</th>
        <th className="py-1 pr-2">Lead time</th>
        <th className="py-1 pr-2">Match</th>
        <th className="py-1">Source</th>
      </tr>
    </thead>
    <tbody>
      {options.map((o) => (
        <tr
          key={o.productId}
          className={`border-t border-gray-100 align-top ${o.selected ? "bg-blue-50" : ""}`}
        >
          <td className="py-1 pr-2 font-medium">
            {o.vendor.name || o.vendor.supplierId || "—"}
            {o.selected && (
              <Chip className="ml-1 bg-blue-100 text-blue-800">
                On this quote
              </Chip>
            )}
          </td>
          <td className="py-1 pr-2">
            {o.sourceUrl ? (
              <a
                className="text-blue-700 hover:underline"
                href={o.sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                {o.summary || o.productId}
              </a>
            ) : (
              o.summary || o.productId
            )}
            {o.sourcingNote && (
              <span className="block text-gray-500">{o.sourcingNote}</span>
            )}
          </td>
          <td className="py-1 pr-2 whitespace-nowrap">
            {o.price
              ? `${o.price.value.toLocaleString(undefined, { style: "currency", currency: o.price.currency || currency })}${o.price.unit ? ` / ${o.price.unit}` : ""}`
              : "—"}
            {o.lineAmount !== null && (
              <span className="block text-gray-500">
                line{" "}
                {o.lineAmount.toLocaleString(undefined, {
                  style: "currency",
                  currency,
                })}
              </span>
            )}
          </td>
          <td className="py-1 pr-2 whitespace-nowrap">
            {o.leadTimeDaysMin !== null || o.leadTimeDaysMax !== null
              ? `${o.leadTimeDaysMin ?? "?"}–${o.leadTimeDaysMax ?? "?"} days`
              : "—"}
          </td>
          <td className="py-1 pr-2">
            {o.exactMatch ? (
              <Chip className="bg-green-100 text-green-800">Exact</Chip>
            ) : o.facets?.length ? (
              <FitTable facets={o.facets} />
            ) : (
              <span className="text-gray-600">
                {o.differences.join("; ") || "Close"}
              </span>
            )}
            {o.notes.length > 0 && (
              <span className="block text-gray-500">{o.notes.join("; ")}</span>
            )}
          </td>
          <td className="py-1 text-gray-600">
            {o.ingestMethod}
            {o.obtainedAt && ` · ${when(o.obtainedAt)}`}
            <span className="block text-gray-500">
              {o.priceOrigin}
              {o.vendorOriginalPrice &&
                ` (vendor's original price ${o.vendorOriginalPrice.value.toLocaleString(undefined, { style: "currency", currency: o.vendorOriginalPrice.currency || currency })}${o.vendorOriginalPrice.unit ? ` / ${o.vendorOriginalPrice.unit}` : ""})`}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SuggestionsList = ({
  suggestions,
  requests,
}: {
  suggestions: RfqVendorSuggestion[];
  requests: QuoteVendorRequestView[];
}) => (
  <ul className="mt-1 space-y-1 text-xs">
    {suggestions.map((s) => {
      const request = requests.find((r) => r.supplierId === s.supplierId);
      return (
        <li key={s.supplierId}>
          <span className="font-medium">{s.name}</span>{" "}
          <Chip
            className={SOURCE_STYLE[s.source || "inventory"]}
            title={[
              s.sourceDetail,
              s.sourceUpdatedAt
                ? `updated ${new Date(s.sourceUpdatedAt).toLocaleString()}`
                : "",
            ]
              .filter(Boolean)
              .join(" — ")}
          >
            {sourceLabel(s)}
            {s.sourceUpdatedAt && ` · updated ${when(s.sourceUpdatedAt)}`}
          </Chip>{" "}
          <VendorProvenanceChip vendor={s} />
          {request ? (
            <>
              {" "}
              <Chip className={STATUS_STYLE[request.status]}>
                Contacted · {STATUS_LABEL[request.status] || request.status}
              </Chip>
            </>
          ) : (
            <>
              {" "}
              <Chip className="bg-gray-50 text-gray-500">Not contacted</Chip>
            </>
          )}
          {!s.hasEmail && (
            <span className="text-gray-400"> (no email on file)</span>
          )}
          <span className="text-gray-500"> — {s.reasons.join("; ")}</span>
        </li>
      );
    })}
  </ul>
);

const Block = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mt-2">
    <p className="text-[0.65rem] font-medium uppercase tracking-wide text-gray-500">
      {title}
    </p>
    {children}
  </div>
);

const money = (price: ProductInsight["price"]["current"]): string =>
  price?.value == null
    ? "no price"
    : `${price.currency || "USD"} ${Number(price.value).toFixed(2)}${price.unit ? `/${price.unit}` : ""}`;

/**
 * One line of the facts a super user would otherwise click the shield for:
 * where the record came from and when, the price and where it came from.
 */
const InsightSummary = ({ insight }: { insight: ProductInsight }) => {
  const obtained = insight.dataOrigin?.obtainedAt;
  const confirmed = insight.dataOrigin?.vendorConfirmedAt;
  return (
    <div className="mt-0.5 text-xs text-gray-700">
      <span className="text-gray-500">Data:</span>{" "}
      {insight.dataOrigin?.method || "unknown source"}
      {obtained ? `, obtained ${when(obtained)}` : ", date unknown"}
      {confirmed && confirmed !== obtained
        ? ` (vendor confirmed ${when(confirmed)})`
        : ""}
      {" · "}
      <span className="text-gray-500">Price:</span>{" "}
      {money(insight.price.current)} — {insight.price.origin}
      {insight.price.manuallySet ? " (set by hand)" : ""}
      {insight.price.estimates?.computedAt
        ? `, priced ${when(insight.price.estimates.computedAt)}`
        : ""}
    </div>
  );
};

/**
 * The full super-user view of the quoted item, inline: price story, record
 * history, fields and recent edits. Editing still goes through the super-user
 * panel, which needs the recent re-authentication the shield asks for.
 */
const ItemDetail = ({
  insight,
  onChanged,
}: {
  insight: ProductInsight;
  onChanged: () => void;
}) => {
  const superUserActive = useSuperUserMode((state) => state.active);
  const [editing, setEditing] = useState(false);
  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[0.65rem] font-medium uppercase tracking-wide text-gray-500">
          Item detail (super powers) · product {insight.productId}
        </p>
        {superUserActive ? (
          <button
            type="button"
            className="text-xs text-blue-700 hover:underline"
            onClick={() => setEditing(true)}
          >
            Edit price & fields
          </button>
        ) : (
          <span className="text-[0.65rem] text-gray-500">
            Turn on super powers (shield, top right) to edit.
          </span>
        )}
      </div>
      <PriceStory insight={insight} className="mt-2" />
      <ItemFieldsSummary fields={insight.fields} className="mt-2" />
      {insight.provenance && (
        <ProvenanceSection provenance={insight.provenance} />
      )}
      <RecentChanges audit={insight.audit} className="mt-3" />
      {editing && (
        <SuperUserPricePanel
          productId={insight.productId}
          onClose={() => setEditing(false)}
          onSaved={onChanged}
        />
      )}
    </div>
  );
};

const ItemSourcing = ({
  item,
  currency,
  onChanged,
}: {
  item: QuoteItemSourcing;
  currency: string;
  onChanged: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const contacted = item.vendorRequests.length;
  const replied = item.vendorRequests.filter(
    (r) => r.status === "responded",
  ).length;
  return (
    <li className="rounded border border-gray-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium text-[#00062e]">{item.summary}</span>
          <div className="mt-0.5 text-xs text-gray-700">
            Vendor on the quote:{" "}
            <span className="font-medium">{item.vendor.name || "unknown"}</span>
            {item.vendorRequest && (
              <>
                {" "}
                <Chip className={STATUS_STYLE[item.vendorRequest.status]}>
                  RFQ'd ·{" "}
                  {STATUS_LABEL[item.vendorRequest.status] ||
                    item.vendorRequest.status}
                  {item.vendorRequest.respondedAt
                    ? ` ${when(item.vendorRequest.respondedAt)}`
                    : ""}
                </Chip>
              </>
            )}
            {item.line && (
              <>
                {" "}
                <Chip className={VERDICT_STYLE[item.line.verdict]}>
                  {VERDICT_LABEL[item.line.verdict]}
                </Chip>
              </>
            )}
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            {item.line
              ? `RFQ line: ${[item.line.material, item.line.shape, item.line.standard].filter(Boolean).join(" · ")} · ${item.options.length} platform option${item.options.length === 1 ? "" : "s"} · ${item.suggestions.length} vendor${item.suggestions.length === 1 ? "" : "s"} suggested · ${contacted} contacted${contacted ? ` (${replied} replied)` : ""}`
              : "Not linked to an RFQ line — quoted straight from the catalog."}
          </div>
          {item.insight ? (
            <InsightSummary insight={item.insight} />
          ) : (
            <div className="mt-0.5 text-xs text-gray-500">
              {item.productId
                ? "This item is no longer in inventory — no data or price detail to show."
                : "No platform product behind this item."}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {item.insight && (
            <button
              type="button"
              className="text-xs text-blue-700 hover:underline"
              onClick={() => setDetailOpen(!detailOpen)}
            >
              {detailOpen ? "Hide item detail" : "Item detail (super powers)"}
            </button>
          )}
          {item.line && (
            <button
              type="button"
              className="text-xs text-blue-700 hover:underline"
              onClick={() => setOpen(!open)}
            >
              {open ? "Hide options & vendors" : "All options & vendors"}
            </button>
          )}
        </div>
      </div>
      {detailOpen && item.insight && (
        <ItemDetail insight={item.insight} onChanged={onChanged} />
      )}
      {open && item.line && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          <Block title={`Platform options (${item.options.length})`}>
            {item.options.length ? (
              <OptionsTable options={item.options} currency={currency} />
            ) : (
              <p className="text-xs text-gray-500">
                Nothing on the platform fills this line today.
              </p>
            )}
          </Block>
          <Block title={`Vendors suggested (${item.suggestions.length})`}>
            {item.suggestions.length ? (
              <SuggestionsList
                suggestions={item.suggestions}
                requests={item.vendorRequests}
              />
            ) : (
              <p className="text-xs text-gray-500">No vendor suggestions.</p>
            )}
          </Block>
          <Block title={`Vendors contacted (${item.vendorRequests.length})`}>
            {item.vendorRequests.length ? (
              <ul className="mt-1 space-y-1">
                {item.vendorRequests.map((r) => (
                  <li key={r.id} className="text-xs">
                    <span className="font-medium">
                      {r.supplierName || r.supplierId}
                    </span>{" "}
                    <RequestStatus request={r} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500">
                No vendor was emailed for this line.
              </p>
            )}
          </Block>
        </div>
      )}
    </li>
  );
};

const OtherLine = ({
  entry,
  currency,
}: {
  entry: QuoteLineSourcing;
  currency: string;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <li className="rounded border border-dashed border-gray-300 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium text-[#00062e]">
            {entry.line.description}
          </span>{" "}
          <Chip className={VERDICT_STYLE[entry.verdict]}>
            {VERDICT_LABEL[entry.verdict]}
          </Chip>
          <div className="mt-0.5 text-xs text-gray-500">
            Not on this quote — {entry.vendorRequests.length} vendor
            {entry.vendorRequests.length === 1 ? "" : "s"} contacted,{" "}
            {
              entry.vendorRequests.filter((r) => r.status === "responded")
                .length
            }{" "}
            replied.
          </div>
        </div>
        <button
          type="button"
          className="text-xs text-blue-700 hover:underline"
          onClick={() => setOpen(!open)}
        >
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
      {open && (
        <div className="mt-2 border-t border-gray-100 pt-2">
          {entry.options.length > 0 && (
            <Block title={`Platform options (${entry.options.length})`}>
              <OptionsTable options={entry.options} currency={currency} />
            </Block>
          )}
          <Block title={`Vendors suggested (${entry.suggestions.length})`}>
            {entry.suggestions.length ? (
              <SuggestionsList
                suggestions={entry.suggestions}
                requests={entry.vendorRequests}
              />
            ) : (
              <p className="text-xs text-gray-500">No vendor suggestions.</p>
            )}
          </Block>
          <Block title={`Vendors contacted (${entry.vendorRequests.length})`}>
            {entry.vendorRequests.length ? (
              <ul className="mt-1 space-y-1">
                {entry.vendorRequests.map((r) => (
                  <li key={r.id} className="text-xs">
                    <span className="font-medium">
                      {r.supplierName || r.supplierId}
                    </span>{" "}
                    <RequestStatus request={r} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500">
                No vendor was emailed for this line.
              </p>
            )}
          </Block>
        </div>
      )}
    </li>
  );
};

export const QuoteSourcingSection = ({
  quoteId,
  currency,
}: {
  quoteId: string;
  currency: string;
}) => {
  const [data, setData] = useState<QuoteSourcing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shown, setShown] = useState(false);

  const load = async (force = false) => {
    setShown(true);
    if ((data && !force) || loading) return;
    setLoading(true);
    setError("");
    try {
      setData(await loadQuoteSourcing(quoteId));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#00062e]">
            Sourcing behind this quote{" "}
            <span className="font-normal text-gray-500">(Kono only)</span>
          </h2>
          <p className="mt-0.5 text-xs text-gray-600">
            Per item: the vendor filling it, where its data and price came from
            and when (the super-powers detail), every platform option, the
            vendors suggested (with the source and date of each recommendation)
            and the vendors contacted with their status.
          </p>
        </div>
        {!shown && (
          <button
            type="button"
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm hover:bg-gray-100"
            onClick={() => void load()}
          >
            Show sourcing
          </button>
        )}
        {shown && data && (
          <button
            type="button"
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm hover:bg-gray-100"
            onClick={() => void load(true)}
            disabled={loading}
          >
            Refresh
          </button>
        )}
      </div>
      {loading && (
        <p className="mt-2 text-sm text-gray-500">
          Matching inventory and vendors for every line…
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {data && (
        <div className="mt-3">
          <p className="text-xs text-gray-600">
            {data.rfq ? (
              <>
                From RFQ{" "}
                <a
                  className="text-blue-700 hover:underline"
                  href={data.rfq.link}
                >
                  {data.rfq.id}
                </a>{" "}
                · status {data.rfq.status}
                {data.rfq.submittedAt &&
                  ` · submitted ${when(data.rfq.submittedAt)}`}{" "}
                · {data.rfq.customerEmail}
              </>
            ) : (
              "No RFQ is linked to this quote — it was saved straight from the cart, so only the quoted vendors are known."
            )}
          </p>
          <ul className="mt-2 space-y-2">
            {data.items.map((item) => (
              <ItemSourcing
                key={item.index}
                item={item}
                currency={currency}
                onChanged={() => void load(true)}
              />
            ))}
          </ul>
          {data.otherLines.length > 0 && (
            <>
              <p className="mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
                RFQ lines not on this quote ({data.otherLines.length})
              </p>
              <ul className="mt-1 space-y-2">
                {data.otherLines.map((entry) => (
                  <OtherLine
                    key={entry.line.id}
                    entry={entry}
                    currency={currency}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
};
